/**
 * ChatPanel Component
 *
 * Slide-out side panel for the "Ask Luminaria" AI assistant.
 */

import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';
import { usePageContext } from '@app/contexts/PageContext';
import { getErrorMessage } from '@shared/api/errors';
import { useSendMessage } from '@shared/hooks/useChat';
import { ChatInput } from './ChatInput';
import { ChatMessage } from './ChatMessage';
import { useChatState } from './useChatState';

export interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({ isOpen, onClose }) => {
  const { messages, addMessage, clearMessages } = useChatState();
  const { mutate: sendMessage, isPending } = useSendMessage();
  const { pageContext } = usePageContext();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleSendMessage = (content: string) => {
    // Add user message immediately
    addMessage({ role: 'user', content });

    // Error notices were generated here, not by the model: leave them out
    // of the history so the model never sees fabricated assistant turns.
    const conversationHistory = messages
      .filter((message) => !message.isError)
      .slice(-20) // Send last 20 messages for context
      .map(({ role, content: text }) => ({ role, content: text }));

    // Send to API with page context if available
    sendMessage(
      {
        message: content,
        conversationHistory,
        pageContext: pageContext || undefined, // Include current page context
      },
      {
        onSuccess: (data) => {
          addMessage({ role: 'assistant', content: data.reply });
        },
        onError: (error) => {
          // The server's message (e.g. the rate-limit explanation), not axios's status line
          addMessage({
            role: 'assistant',
            content: `Error: ${getErrorMessage(error)}`,
            isError: true,
          });
        },
      }
    );
  };

  const handleClearChat = () => {
    if (confirm('Are you sure you want to clear this conversation?')) {
      clearMessages();
    }
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[1099] transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        className={clsx(
          'fixed top-0 right-0 h-full bg-surface border-l border-border shadow-2xl z-[1100] flex flex-col transition-transform duration-300',
          'w-full md:w-[600px]',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-bg-alt">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-text">Ask Luminaria</span>
            <span className="text-xs px-2 py-0.5 bg-primary bg-opacity-10 text-primary rounded-full font-medium">
              Beta
            </span>
          </div>
          <div className="flex items-center gap-2">
            {messages.length > 0 && (
              <button
                onClick={handleClearChat}
                className="text-sm text-text-muted hover:text-text transition-colors px-2 py-1"
                title="Clear conversation"
              >
                Clear
              </button>
            )}
            <button
              onClick={onClose}
              className="text-2xl text-text-muted hover:text-text transition-colors leading-none"
              aria-label="Close chat"
            >
              ×
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 bg-bg">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <div className="text-4xl mb-4">💡</div>
              <h3 className="text-lg font-semibold text-text mb-2">
                How can I help you today?
              </h3>
              <p className="text-sm text-text-muted mb-6">
                Ask me about your portfolios, tax estimates, realized gains, or
                investment strategies.
              </p>
              <div className="space-y-2 w-full max-w-sm">
                <button
                  onClick={() =>
                    handleSendMessage('What portfolios do I have?')
                  }
                  className="w-full px-4 py-2 bg-surface border border-border rounded-md text-sm text-text hover:bg-bg-alt transition-colors text-left"
                >
                  What portfolios do I have?
                </button>
                <button
                  onClick={() =>
                    handleSendMessage("What's my tax liability this year?")
                  }
                  className="w-full px-4 py-2 bg-surface border border-border rounded-md text-sm text-text hover:bg-bg-alt transition-colors text-left"
                >
                  What's my tax liability this year?
                </button>
                <button
                  onClick={() =>
                    handleSendMessage('Show me my realized gains for 2024')
                  }
                  className="w-full px-4 py-2 bg-surface border border-border rounded-md text-sm text-text hover:bg-bg-alt transition-colors text-left"
                >
                  Show me my realized gains for 2024
                </button>
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg, index) => (
                <ChatMessage key={index} role={msg.role} content={msg.content} />
              ))}
              {isPending && (
                <div className="flex justify-start mb-4">
                  <div className="max-w-[80%] rounded-lg px-4 py-2.5 bg-surface border border-border text-text">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-text-muted rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                        <span className="w-2 h-2 bg-text-muted rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                        <span className="w-2 h-2 bg-text-muted rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                      </div>
                      <span className="text-sm text-text-muted">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        <ChatInput
          onSend={handleSendMessage}
          disabled={isPending}
          placeholder={
            messages.length === 0
              ? 'Ask about your portfolios, taxes, or options...'
              : 'Type your message...'
          }
        />
      </div>
    </>
  );
};
